import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/Button';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { NewsletterModal } from '@/components/ui/NewsletterModal';
import { useSiteSettings } from '@/contexts/SiteSettingsContext';
import { handleDiscoveryCallClick } from '@/components/layout/Navbar';

const LocalTime = ({ timeZone }: { timeZone: string }) => {
  const [time, setTime] = useState("");

  useEffect(() => {
    const updateTime = () => {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone,
          hour: 'numeric',
          minute: '2-digit',
          timeZoneName: 'short'
        });
        setTime(formatter.format(new Date()));
      } catch (e) {
        // Silent fallback
      }
    };
    
    updateTime();
    const interval = setInterval(updateTime, 30000); 
    
    return () => clearInterval(interval);
  }, [timeZone]);

  if (!time) return null;
  return <span className="inline-block text-[10px] opacity-60 ml-1.5 font-mono tracking-tighter uppercase">[{time}]</span>;
};

export function Footer() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { links } = useSiteSettings();

  return (
    <footer className="bg-gradient-to-br from-ink to-ink/90 text-ground/90 pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Pre-footer CTA */}
        <div className="border-b border-ground/10 pb-20 mb-16 text-center max-w-3xl mx-auto">
          <h2 id="team-effectiveness-text" className="text-4xl md:text-5xl font-bold font-serif leading-tight mb-6 text-ground drop-shadow-md tracking-wide">
            {t('footer.title', 'Ready to improve your approach to people development & team effectiveness?')}
          </h2>
          <p className="text-lg text-ground/80 font-light leading-relaxed mb-10">
            {t('footer.desc', "Let's connect. Whether you're a coach looking to deepen your practice or a leader wanting to bring out the best in your team, we're here to help.")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href={links.bookingUrl}
              onClick={() => handleDiscoveryCallClick(links.bookingUrl)}
              className="w-full sm:w-auto inline-flex items-center justify-center bg-white text-ink hover:bg-ground-deep h-12 px-8 uppercase tracking-widest text-xs font-bold transition-colors"
            >
              {t('footer.btnBook', 'Book a Discovery Call')}
            </a>
            <Link to="/evidence-and-research" className="w-full sm:w-auto"><Button variant="outline" size="lg" className="w-full border-white text-white hover:bg-white hover:text-ink">
              {t('footer.btnFact', 'Evidence & Research')}
            </Button></Link>
            <Link to="/framework" className="w-full sm:w-auto"><Button variant="ghost" size="lg" className="w-full text-ground hover:text-white hover:bg-white/5 border border-transparent">
              {t('footer.btnDiscover', 'Discover Our Framework')}
            </Button></Link>
          </div>
        </div>

        {/* Footer Links */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 mb-16">
          <div className="col-span-2 md:col-span-1 lg:col-span-2">
            <span className="font-serif text-2xl font-bold tracking-tight text-ground uppercase block mb-6">
              FIVE LENS
            </span>
            <p className="text-sm text-ground/60 font-light leading-relaxed max-w-xs">
              {t('footer.brandDesc', 'A multidimensional approach to human potential, integrating the Enneagram with four additional lenses of psychological insight.')}
            </p>
            <NewsletterModal />
          </div>
          
          <div>
            <span className="block font-semibold text-ground uppercase tracking-wider text-sm mb-6" role="heading" aria-level={2}>{t('footer.solTitle', 'Solutions')}</span>
            <ul className="space-y-4 text-sm font-light text-ground/70">
              <li>
                <Link to="/for-individuals" className="hover:text-rule-3 transition-colors block">
                  For Individuals
                </Link>
                <ul className="pl-4 mt-2 space-y-2 border-l border-ground/20 ml-2">
                  <li><Link to="/for-individuals#request-intro" className="hover:text-rule-3 transition-colors text-xs">Request a Debrief</Link></li>
                  <li><Link to="/find-a-practitioner" className="hover:text-rule-3 transition-colors text-xs">Find a Coach</Link></li>
                </ul>
              </li>
              <li>
                <Link to="/practitioner-certification" className="hover:text-rule-3 transition-colors block">
                  For Coaches and Practitioners
                </Link>
                <ul className="pl-4 mt-2 space-y-2 border-l border-ground/20 ml-2">
                  <li><Link to="/cohorts" className="hover:text-rule-3 transition-colors text-xs">Upcoming Cohorts</Link></li>
                  <li><Link to="/icf-cce-points" className="hover:text-rule-3 transition-colors text-xs">ICF CCE Points</Link></li>
                </ul>
              </li>
              <li>
                <Link to="/organizations" className="hover:text-rule-3 transition-colors block">
                  {t('footer.sol1', 'For Organisations & HR Teams')}
                </Link>
                <ul className="pl-4 mt-2 space-y-2 border-l border-ground/20 ml-2">
                  <li><Link to="/team-effectiveness" className="hover:text-rule-3 transition-colors text-xs">{t('footer.sol4', 'Team Alignment')}</Link></li>
                  <li><Link to="/personal-mastery-and-resilience" className="hover:text-rule-3 transition-colors text-xs">Personal Mastery</Link></li>
                </ul>
              </li>
            </ul>
          </div>

          <div>
            <span className="block font-semibold text-ground uppercase tracking-wider text-sm mb-6" role="heading" aria-level={2}>{t('footer.frameTitle', 'The Framework')}</span>
            <ul className="space-y-4 text-sm font-light text-ground/70">
              <li><Link to="/lens/enneagram" className="hover:text-rule-3 transition-colors">{t('footer.frame1', 'The Enneagram')}</Link></li>
              <li><Link to="/lens/personal-mastery" className="hover:text-rule-3 transition-colors">{t('footer.frame2', 'Personal Mastery & Maturity')}</Link></li>
              <li><Link to="/lens/emotional-resilience" className="hover:text-rule-3 transition-colors">{t('footer.frame3', 'Emotional Resilience')}</Link></li>
              <li><Link to="/lens/social-drives" className="hover:text-rule-3 transition-colors">{t('footer.frame4', 'Social Drives')}</Link></li>
              <li><Link to="/lens/energy-centres" className="hover:text-rule-3 transition-colors">{t('footer.frame5', 'Energy Centres')}</Link></li>
            </ul>
          </div>

          <div>
            <span className="block font-semibold text-ground uppercase tracking-wider text-sm mb-6" role="heading" aria-level={2}>Assessments & Reports</span>
            <ul className="space-y-4 text-sm font-light text-ground/70">
              <li><Link to="/team-maturity-assessment" className="hover:text-rule-3 transition-colors">Team Assessment</Link></li>
              <li><Link to="/evidence-and-research" className="hover:text-rule-3 transition-colors">Evidence & Research</Link></li>
              <li><Link to="/reports/standard" className="hover:text-rule-3 transition-colors">Standard Report</Link></li>
              <li><Link to="/reports/premium" className="hover:text-rule-3 transition-colors">Premium Report</Link></li>
              <li><Link to="/reports/team" className="hover:text-rule-3 transition-colors">Team Report</Link></li>
              <li><Link to="/reports/team-leader" className="hover:text-rule-3 transition-colors">Team Leader Report</Link></li>
              <li><Link to="/resource-library" className="hover:text-rule-3 transition-colors">Resource Library</Link></li>
            </ul>
          </div>

          <div>
            <span className="block font-semibold text-ground uppercase tracking-wider text-sm mb-6" role="heading" aria-level={2}>{t('footer.compTitle', 'Company')}</span>
            <ul className="space-y-4 text-sm font-light text-ground/70">
              <li><Link to="/about" className="hover:text-rule-3 transition-colors">{t('footer.comp1', 'About Us')}</Link></li>
              <li><Link to="/contact" className="hover:text-rule-3 transition-colors">{t('footer.comp2', 'Contact')}</Link></li>
              <li><Link to="/blog" className="hover:text-rule-3 transition-colors">{t('nav.blog', 'Blog')}</Link></li>
              <li><Link to="/icf-cce-points" className="hover:text-rule-3 transition-colors">ICF CCE Points</Link></li>
              <li><Link to="/privacy-policy" className="hover:text-rule-3 transition-colors">{t('footer.comp3', 'Privacy Policy')}</Link></li>
              <li><Link to="/terms-of-service" className="hover:text-rule-3 transition-colors">{t('footer.comp4', 'Terms of Service')}</Link></li>
              <li><a href="https://portal.enneainternational.com" className="hover:text-rule-3 transition-colors">Portal Login</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-ground/10 pt-8 flex flex-col md:flex-row items-start justify-between gap-6 text-xs font-light text-ground/50 pb-16 md:pb-0">
          <p className="mt-1">&copy; {new Date().getFullYear()} {t('footer.rights', 'FIVE LENS Enneagram. All rights reserved.')}</p>
          <div className="flex flex-col items-start gap-2 text-left md:mr-24 lg:mr-32">
            <p>Offices in Greater Philadelphia, United States & Cape Town, South Africa. Operating Globally.</p>
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mt-1">
              <a href="https://www.linkedin.com/in/lindbergonline" target="_blank" rel="noopener noreferrer" className="hover:text-rule-3 transition-colors flex items-center">
                Contact Philadelphia
                <LocalTime timeZone="America/New_York" />
              </a>
              <span className="hidden sm:inline text-ground/30">|</span>
              <a href="https://www.linkedin.com/in/hymiegordon" target="_blank" rel="noopener noreferrer" className="hover:text-rule-3 transition-colors flex items-center">
                Contact Cape Town
                <LocalTime timeZone="Africa/Johannesburg" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
